import { useState, useEffect } from 'react';
import { ChevronRight, Bell, Send, Tag } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { GROUPS_LIST, NICHE_IMAGES } from '../../utils/constants';
import GroupDealsDrawer from '../groups/GroupDealsDrawer';
import AddDealDrawer from '../groups/AddDealDrawer';

export default function StoresTab() {
  const {
    isolatedGroupDeals,
    joinedGroupIds,
    setShowNotifications,
    unreadCount,
    getGroupMessages,
    addGroupMessage,
    profileForm,
    showGroupDealsDrawer,
    setShowGroupDealsDrawer,
    setSelectedGroup,
  } = useAppState();
  const { user } = useAuth();
  const { isDesktop } = useResponsive();

  // Get time-based greeting (matches HomeTab)
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const userFirstName = user?.first_name || profileForm?.firstName || 'there';

  const [newMessage, setNewMessage] = useState('');

  // Use the first joined group as the default group for the chat
  const defaultGroup = GROUPS_LIST.find((g) => joinedGroupIds.includes(g.id)) || GROUPS_LIST[0];
  const deals = isolatedGroupDeals[defaultGroup?.id] || [];
  const messages = defaultGroup ? getGroupMessages(defaultGroup.id) : [];

  // Set the selected group in context so drawers can access it
  useEffect(() => {
    if (defaultGroup) {
      setSelectedGroup(defaultGroup);
    }
  }, [defaultGroup, setSelectedGroup]);

  const handleSendMessage = () => {
    if (!newMessage.trim() || !defaultGroup) return;

    addGroupMessage(defaultGroup.id, {
      type: 'message',
      author: profileForm.firstName || 'You',
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${profileForm.firstName || 'You'}`,
      text: newMessage,
    });
    setNewMessage('');
  };

  return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {/* Mobile Header - matches HomeTab */}
          {!isDesktop && (
            <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
              <div className="flex items-center gap-3">
                <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
                <h1 className="font-bold text-gray-900 text-lg">
                  {getGreeting()}, {userFirstName}
                </h1>
              </div>
              <button
                onClick={() => setShowNotifications(true)}
                className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
              >
                <Bell size={22} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
                )}
              </button>
            </header>
          )}
          <div className={`px-4 py-4 ${isDesktop ? 'max-w-4xl mx-auto' : 'pb-24'}`}>
            {/* Group Chat Header */}
            {defaultGroup && (
              <div className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm mb-4">
                <img
                  src={NICHE_IMAGES[defaultGroup.niche]?.[0] || NICHE_IMAGES.general[0]}
                  alt={defaultGroup.name}
                  className="w-10 h-10 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <p className="font-bold text-gray-900 truncate">{defaultGroup.name}</p>
                  <p className="text-sm text-gray-500">{defaultGroup.members} members • {deals.length} deals</p>
                </div>
              </div>
            )}

            {/* Interactive Active Deals Banner */}
            {deals.length > 0 && (
              <button
                onClick={() => setShowGroupDealsDrawer(true)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow text-left mb-4"
              >
                {/* Stacked Logo Avatars */}
                <div className="flex -space-x-3">
                  {deals.slice(0, 3).map((deal, index) => (
                    <div
                      key={deal.id}
                      className={`w-10 h-10 rounded-full border-2 border-white ${deal.color || 'bg-indigo-600'} flex items-center justify-center text-white text-xs font-black shadow-sm`}
                      style={{ zIndex: 3 - index }}
                    >
                      {deal.logo}
                    </div>
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">Active Deals</p>
                  <p className="text-sm text-gray-500">Tap to view {deals.length} offer{deals.length !== 1 ? 's' : ''}</p>
                </div>
                <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
              </button>
            )}

            {/* Chat Messages Area */}
            <div className="space-y-3">
              {messages.length === 0 ? (
                /* Empty State */
                <div className="w-full p-4 bg-white rounded-2xl shadow-sm text-center">
                  <p className="text-sm text-gray-500">Start coordinating with your group!</p>
                  <p className="text-xs text-gray-400 mt-1">Messages will appear here</p>
                </div>
              ) : (
                /* Messages List */
                messages.map((msg) => (
                  <div key={msg.id} className="flex gap-3">
                    <img
                      src={msg.avatar}
                      alt={msg.author}
                      className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-gray-900">{msg.author}</span>
                        {msg.type === 'deal_comment' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full">
                            <Tag size={10} />
                            {msg.dealBrand}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{msg.time}</span>
                      </div>
                      <div className="bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm">
                        <p className="text-sm text-gray-700">{msg.text}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Input - fixed on mobile, flexbox on desktop */}
        {!isDesktop ? (
          <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-0 right-0 bg-white border-t border-gray-100 z-20 px-4 py-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Message group..."
                className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="shrink-0 bg-white border-t border-gray-100">
            <div className="px-4 py-3 max-w-4xl mx-auto">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Message group..."
                  className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drawers */}
        <GroupDealsDrawer />
        <AddDealDrawer />
      </div>
  );
}
